import './NotificationItem.scss';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { Component } from '@components/Component/Component.js';
import { ButtonsMixin } from '@components/Mixin/ButtonsMixin/ButtonsMixin.js';
import { DateTimeManager } from "@components/DateTimeManager/DateTimeManager.js";
import { parseXml, zoomHandler } from "@components/Utility/index.js";
import { ENV } from "@components/Environment.js";
import { eventContextGet } from '@components/EventConfigManager/EventConfigManagerUtil.js';
/**
 * Class `NotificationItem` extends the `Component` class.
 */
export class NotificationItem extends ButtonsMixin(Component) {
    get notificationId() {
        return this.data.notificationid;
    }
    get message() {
        return this.data.message;
    }
    set message(s) {
        this.data.message = s;
        this.renderMessage();
    }
    renderMessage() {
        if (this.drawn) {
            this.rootElement.querySelector('.notification-message').innerText = this.message;
        }
    }
    get subject() {
        return this.data.subject;
    }
    set subject(s) {
        this.data.subject = s;
        this.renderSubject();
    }
    renderSubject() {
        if (this.drawn) {
            this.rootElement.querySelector('.notification-subject').innerText = this.subject;
        }
    }
    get timestamp() {
        return this.data.timestamp;
    }
    get xmlParams() {
        return this.data.xmlparams;
    }
    set elapsed(val) {
        this._elapsed = val;
    }
    get elapsed() {
        return this._elapsed;
    }
    set itemWidth(val) {
        this._itemWidth = val;
    }
    get itemWidth() {
        return this._itemWidth;
    }
    constructor(id, rootElement, dependencies = {}) {
        super(id, rootElement, dependencies);
        this.componentName = 'NotificationItem';
        this.rootElementType = 'div';
        this._elapsed = '';
        this.data = dependencies.data;
        delete dependencies.data;
        this.initializeButtons();
        this.calculateElapsed();
    }
    initializeButtons() {
        // the buttons will be displayed from left to right in the order they are added.
        this.buttonAdd({
            icon: 'close',
            id: 'dismiss',
            onClick: () => {
                this.dismiss();
            }
        });
    }
    draw() {
        super.draw();
        // need a click handler and logic to recreate the handling of the fields in this.data to generate the link etc.
        const buttonContainer = this.rootElement.querySelector('.showmore-button-container');
        this.buttonContainers.set('showmore', buttonContainer);
        this.calculateMessageOverflow();
        this.setupLinks();
        this.setupTooltips();
    }
    calculateElapsed(refreshDom = false) {
        const now = DateTimeManager.now();
        const luxonTimestamp = DateTimeManager.fromFormat(this.timestamp);
        const diffMinutes = DateTimeManager.dateDiff(luxonTimestamp, now, 'minutes');
        let elapsed;
        switch (true) {
            case diffMinutes < 60:
                elapsed = Math.floor(diffMinutes) + 'm';
                break;
            case diffMinutes >= 60 && diffMinutes <= 1440:
                elapsed = Math.floor(diffMinutes / 60) + 'h';
                break;
            default:
                elapsed = DateTimeManager.formatDateTime(luxonTimestamp, ENV('datetimeFormat'));
                break;
        }
        this.elapsed = elapsed;
        if (refreshDom && this.rootElement) {
            const elem = this.rootElement.querySelector('.notification-elapsed>span');
            if (elem) {
                elem.textContent = elapsed;
            }
        }
    }
    calculateMessageOverflow() {
        const messageContainer = this.rootElement.querySelector('.notification-message-container');
        const messageContent = messageContainer?.querySelector('.notification-message');
        if (!messageContainer || !messageContent || this._expanded) {
            return;
        }
        // only do the calculation if the width changed
        if (this.itemWidth && messageContent.clientWidth === this.itemWidth) {
            return;
        }
        this.itemWidth = messageContent.clientWidth;
        if (messageContent.clientHeight > 50) {
            messageContainer.classList.add('has-overflow');
            this.buttonAdd({
                id: 'showmore',
                icon: 'more_horiz',
                containerId: 'showmore',
                onClick: () => {
                    this.showFullMessage();
                }
            });
        }
        else {
            messageContainer.classList.remove('has-overflow');
            this.buttonRemove('showmore');
        }
    }
    setupLinks() {
        let url;
        if (!this.data.xmlparams) {
            return;
        }
        const config = parseXml(this.xmlParams);
        if (!config.openform && !config.openurl && !config.eventconfig) {
            return;
        }
        // if (config.eventconfig) {
        // 	DIContainer.getInstance<EventConfigManagerInterface>('EventConfigManager')
        // 		.parse(config.eventconfig, eventContextGet({initiator: this}));
        //
        // 	return;
        // } else if (config.openform){
        // 	const {formname, keyvalue, keyname} = config.openform
        // 	url = `form/${formname}?${keyname}=${keyvalue}`
        // } else if (config.openurl) {
        // 	url = config.openurl.url
        // }
        const subject = this.rootElement.querySelector('.notification-subject>span');
        const icon = DIContainer.getInstance('IconManager').getIconElement('open_in_new');
        subject.prepend(icon);
        // subject.setAttribute('data-zoom-url', url)
        subject.classList.add('link');
    }
    handleClick(event) {
        let url;
        if (!this.data.xmlparams) {
            return;
        }
        const config = parseXml(this.xmlParams);
        if (!config.openform && !config.openurl && !config.eventconfig) {
            return;
        }
        if (config.eventconfig) {
            DIContainer.getInstance('EventConfigManager')
                .parse(config.eventconfig, eventContextGet({ initiator: this }))
                .subscribe(() => {
                this.dismiss();
            });
            return;
        }
        else if (config.openform) {
            const { formname, keyvalue, keyname } = config.openform;
            url = `form/${formname}?${keyname}=${keyvalue}`;
        }
        else if (config.openurl) {
            url = config.openurl.url;
        }
        zoomHandler(url, { event });
    }
    setupTooltips() {
        const subject = this.rootElement.querySelector('.notification-subject>span');
        const elapsed = this.rootElement.querySelector('.notification-elapsed>span');
        const formattedDateTime = DateTimeManager.formatDateTime(this.timestamp, ENV('datetimeFormat'));
        if (subject) {
            this.registerTooltip(subject, { tooltip: this.subject, delay: 300 });
        }
        if (elapsed) {
            this.registerTooltip(elapsed, { tooltip: formattedDateTime, delay: 300 });
        }
    }
    showFullMessage() {
        const messageContainer = this.rootElement.querySelector('.notification-message-container');
        this._expanded = true; // so it wont collapse again later if the width changes
        if (messageContainer) {
            messageContainer.classList.remove('has-overflow');
            this.buttonRemove('showmore');
        }
    }
    refreshItem() {
        if (!this.hidden && this.drawn) {
            this.calculateElapsed(true);
            this.calculateMessageOverflow();
        }
    }
    template() {
        return `<div class="notification-content">
<div class="notification-subject" ${!this.subject ? 'style="display:none;"' : ''}><span>${this.subject}</span></div>
<div class="notification-message-container" ${!this.message ? 'style="display:none;"' : ''}>
	<div class="notification-message"><span>${this.message}</span></div>
</div>
<div class="showmore-button-container"></div>
<div class="notification-elapsed"><span>${this.elapsed}</span></div>
</div>`;
    }
    // the list will be informed automatically to remove it this item
    dismiss(clearAll = false) {
        if (!clearAll) {
            // When clearing all notifications, the manager will clear all items simultaneously so we don't need to send multiple requests
            DIContainer.getInstance('NotificationManager')
                .dismissNotifications('popup', [this.notificationId]);
        }
        this.parentComponentGet().componentRemove(this.id);
        this.hidden = true;
        this.destroy();
    }
}
NotificationItem.VERSION = '$Revision: 111977 $';
